// Void Outbox Service for Offline Void Queue Management
import { getStorage } from './storageAdapter';
import { generateUUID } from '@/utils/uuid';
import { errorHandler } from './errorHandler';

/**
 * Enqueue a void operation for offline synchronization
 * @param {Object} voidPayload - Void data to be synced
 * @param {String} storeDB - Store database identifier
 * @param {Object} clientMeta - Client metadata (cashier, appVersion, etc.)
 * @returns {Promise<String>} - Returns the voidId of the enqueued void
 */
export async function enqueueVoid(voidPayload, storeDB, clientMeta = {}) {
    try {
        const storage = await getStorage();
        
        // Generate unique void ID
        const voidId = generateUUID();
        
        const clientMetaWithTimestamp = {
            ...clientMeta,
            clientTimestamp: new Date().toISOString()
        };

        await storage.run(
            `INSERT INTO outbox_voids 
            (void_id, date, status, payload, attempts, last_attempt_date) 
            VALUES (?, ?, ?, ?, ?, ?)`,
            [
                voidId,
                new Date().toISOString(),
                'pending',
                JSON.stringify({ ...voidPayload, storeDB, clientMeta: clientMetaWithTimestamp }),
                0,
                null
            ]
        );

        console.log(`[VoidOutbox] Enqueued void: ${voidId}`);
        return voidId;
    } catch (error) {
        console.error('[VoidOutbox] Failed to enqueue void:', error);
        return null;
    }
}

/**
 * Get pending voids from the outbox (includes both pending and timeout status)
 * @param {Number} batchSize - Maximum number of items to retrieve
 * @returns {Promise<Array>} - Array of pending/timeout voids
 */
export async function getPendingVoids(batchSize = 10) {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT * FROM outbox_voids 
             WHERE status IN ('pending', 'timeout') 
             ORDER BY date ASC 
             LIMIT ?`,
            [batchSize]
        );
        
        return rows.map(row => ({
            voidId: row.void_id,
            date: row.date,
            status: row.status,
            payload: JSON.parse(row.payload),
            numberOfAttempts: row.attempts || 0,
            lastAttemptDate: row.last_attempt_date
        }));
    } catch (error) {
        console.error('[VoidOutbox] Failed to get pending voids:', error);
        return [];
    }
}

/**
 * Mark a void as being sent
 * @param {String} voidId - Void ID
 * @returns {Promise<void>}
 */
export async function markVoidSending(voidId) {
    try {
        const storage = await getStorage();
        await storage.run(
            `UPDATE outbox_voids 
             SET status = 'sending', 
                 attempts = attempts + 1, 
                 last_attempt_date = ?, 
                 updated_at = CURRENT_TIMESTAMP 
             WHERE void_id = ?`,
            [new Date().toISOString(), voidId]
        );
    } catch (error) {
        console.error(`[VoidOutbox] Failed to mark ${voidId} as sending:`, error);
    }
}

/**
 * Mark a void as successfully sent
 * @param {String} voidId - Void ID
 * @param {Object} serverMeta - Server response metadata
 * @returns {Promise<void>}
 */
export async function markVoidSent(voidId, serverMeta = {}) {
    try {
        const storage = await getStorage();
        
        // Get current payload to merge with server metadata
        const row = await storage.get(
            `SELECT payload FROM outbox_voids WHERE void_id = ?`,
            [voidId]
        );
        
        if (row) {
            const currentPayload = JSON.parse(row.payload);
            const updatedPayload = {
                ...currentPayload,
                uploadResponse: serverMeta,
                uploadDate: new Date().toISOString()
            };
            
            await storage.run(
                `UPDATE outbox_voids 
                 SET status = 'sent', 
                     payload = ?,
                     updated_at = CURRENT_TIMESTAMP 
                 WHERE void_id = ?`,
                [JSON.stringify(updatedPayload), voidId]
            );
            console.log(`[VoidOutbox] Marked ${voidId} as sent`);
        }
    } catch (error) {
        console.error(`[VoidOutbox] Failed to mark ${voidId} as sent:`, error);
    }
}

/**
 * Mark a void as failed or timeout
 * @param {String} voidId - Void ID
 * @param {String|Object} reason - Failure reason/error details
 * @returns {Promise<void>}
 */
export async function markVoidFailed(voidId, reason) {
    try {
        const storage = await getStorage();
        
        // Get current attempts count
        const item = await storage.get(
            `SELECT attempts, payload FROM outbox_voids WHERE void_id = ?`,
            [voidId]
        );
        
        if (item) {
            const maxAttempts = 5;
            const status = item.attempts >= maxAttempts ? 'timeout' : 'failed';
            
            const currentPayload = JSON.parse(item.payload);
            const serverMessage =
                (typeof reason === 'string' && reason.trim()
                    ? errorHandler.toPlainErrorText(reason) || reason.trim()
                    : errorHandler.toPlainErrorText(reason)) ||
                (reason && typeof reason === 'object' && reason.message
                    ? String(reason.message)
                    : '') ||
                'Unknown error';
            const updatedPayload = {
                ...currentPayload,
                uploadResponse: {
                    error: serverMessage,
                    failedAt: new Date().toISOString()
                }
            };
            
            await storage.run(
                `UPDATE outbox_voids 
                 SET status = ?, 
                     payload = ?, 
                     last_attempt_date = ?, 
                     updated_at = CURRENT_TIMESTAMP 
                 WHERE void_id = ?`,
                [status, JSON.stringify(updatedPayload), new Date().toISOString(), voidId]
            );
            
            console.log(`[VoidOutbox] Marked ${voidId} as ${status} (attempt ${item.attempts})`);
        }
    } catch (error) {
        console.error(`[VoidOutbox] Failed to mark ${voidId} as failed:`, error);
    }
}

/**
 * Increment retry count for a failed void and reset to pending
 * @param {String} voidId - Void ID
 * @returns {Promise<void>}
 */
export async function incrementVoidRetries(voidId) {
    try {
        const storage = await getStorage();
        await storage.run(
            `UPDATE outbox_voids 
             SET attempts = attempts + 1, 
                 status = 'pending', 
                 last_attempt_date = ?, 
                 updated_at = CURRENT_TIMESTAMP 
             WHERE void_id = ?`,
            [new Date().toISOString(), voidId]
        );
        console.log(`[VoidOutbox] Retry ${voidId}`);
    } catch (error) {
        console.error(`[VoidOutbox] Failed to increment retries for ${voidId}:`, error);
    }
}

/**
 * Count void items by status
 * @param {String} status - Status to count ('pending', 'sending', 'sent', 'failed')
 * @returns {Promise<Number>} - Count of items with given status
 */
export async function countVoidsByStatus(status) {
    try {
        const storage = await getStorage();
        const result = await storage.get(
            `SELECT COUNT(*) as count FROM outbox_voids WHERE status = ?`,
            [status]
        );
        return result ? result.count : 0;
    } catch (error) {
        console.error(`[VoidOutbox] Failed to count items by status ${status}:`, error);
        return 0;
    }
}

/**
 * Get all void outbox items (for admin view)
 * @returns {Promise<Array>} - All void outbox items
 */
export async function getAllVoidOutboxItems() {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT * FROM outbox_voids ORDER BY date DESC`
        );
        
        return rows.map(row => ({
            voidId: row.void_id,
            date: row.date,
            status: row.status,
            payload: JSON.parse(row.payload),
            numberOfAttempts: row.attempts || 0,
            lastAttemptDate: row.last_attempt_date
        }));
    } catch (error) {
        console.error('[VoidOutbox] Failed to get all items:', error);
        return [];
    }
}

/**
 * Get a specific void outbox item by voidId
 * @param {String} voidId - Void ID
 * @returns {Promise<Object|undefined>} - Void outbox item
 */
export async function getVoidOutboxItem(voidId) {
    try {
        const storage = await getStorage();
        const row = await storage.get(
            `SELECT * FROM outbox_voids WHERE void_id = ?`,
            [voidId]
        );
        
        return row ? {
            voidId: row.void_id,
            date: row.date,
            status: row.status,
            payload: JSON.parse(row.payload),
            numberOfAttempts: row.attempts || 0,
            lastAttemptDate: row.last_attempt_date
        } : undefined;
    } catch (error) {
        console.error(`[VoidOutbox] Failed to get item ${voidId}:`, error);
        return undefined;
    }
}

/**
 * Delete a void outbox item (use with caution, only for admin cleanup)
 * @param {String} voidId - Void ID
 * @returns {Promise<void>}
 */
export async function deleteVoidOutboxItem(voidId) {
    try {
        const storage = await getStorage();
        await storage.run(
            `DELETE FROM outbox_voids WHERE void_id = ?`,
            [voidId]
        );
        console.log(`[VoidOutbox] Deleted item: ${voidId}`);
    } catch (error) {
        console.error(`[VoidOutbox] Failed to delete item ${voidId}:`, error);
    }
}

/**
 * Clear all sent void items older than specified days
 * @param {Number} daysOld - Age threshold in days
 * @returns {Promise<Number>} - Number of items deleted
 */
export async function cleanupSentVoids(daysOld = 30) {
    try {
        const storage = await getStorage();
        const threshold = new Date();
        threshold.setDate(threshold.getDate() - daysOld);
        
        const result = await storage.run(
            `DELETE FROM outbox_voids 
             WHERE status = 'sent' AND date < ?`,
            [threshold.toISOString()]
        );
        
        const deletedCount = result.changes || 0;
        console.log(`[VoidOutbox] Cleaned up ${deletedCount} sent items older than ${daysOld} days`);
        return deletedCount;
    } catch (error) {
        console.error('[VoidOutbox] Failed to cleanup sent items:', error);
        return 0;
    }
}

/**
 * Reset void rows stuck in 'sending' (e.g. process killed mid-upload) back to 'pending'.
 * Keeps `attempts` untouched; status guard prevents racing a live send.
 * @param {Number} staleMinutes - Age threshold in minutes (default 10)
 * @returns {Promise<Number>} - Number of rows reset
 */
export async function resetStaleVoidSending(staleMinutes = 10) {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT void_id, last_attempt_date FROM outbox_voids WHERE status = 'sending'`
        );
        const cutoffMs = Math.max(1, staleMinutes) * 60 * 1000;
        const nowMs = Date.now();
        const stale = (rows || []).filter(row => {
            const t = row.last_attempt_date ? new Date(row.last_attempt_date).getTime() : 0;
            return !t || (nowMs - t) > cutoffMs;
        });
        for (const row of stale) {
            await storage.run(
                `UPDATE outbox_voids
                 SET status = 'pending',
                     updated_at = CURRENT_TIMESTAMP
                 WHERE void_id = ? AND status = 'sending'`,
                [row.void_id]
            );
        }
        if (stale.length) {
            console.log(`[VoidOutbox] Reset ${stale.length} stale 'sending' row(s) to pending (staleMinutes=${staleMinutes})`);
        }
        return stale.length;
    } catch (error) {
        console.error('[VoidOutbox] Failed to reset stale sending rows:', error);
        return 0;
    }
}

// Export singleton-like interface
export default {
    enqueueVoid,
    getPendingVoids,
    markVoidSending,
    markVoidSent,
    markVoidFailed,
    incrementVoidRetries,
    countVoidsByStatus,
    getAllVoidOutboxItems,
    getVoidOutboxItem,
    deleteVoidOutboxItem,
    cleanupSentVoids,
    resetStaleVoidSending
};
